import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, Database, Server } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface ConnectionListItemData {
  readonly id: string
  readonly name: string
  readonly databaseType: string
  readonly host: string
  readonly databaseName: string
  readonly status: string
  readonly lastTestedAt?: string
}

export interface ConnectionListCardData {
  readonly message: string
  readonly connections: readonly ConnectionListItemData[]
  readonly pagination?: {
    readonly page: number
    readonly totalItems: number
    readonly pageSize: number
  }
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  INACTIVE: 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
  ERROR: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
}

const DB_TYPE_STYLES: Record<string, string> = {
  POSTGRESQL: 'text-blue-600 dark:text-blue-400',
  MSSQL: 'text-red-600 dark:text-red-400',
  SNOWFLAKE: 'text-cyan-600 dark:text-cyan-400',
  DATABRICKS: 'text-orange-600 dark:text-orange-400',
}

const MAX_VISIBLE = 5

export function ConnectionListCard({ data }: { data: ConnectionListCardData }) {
  const [expanded, setExpanded] = useState(false)

  if (data.connections.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Server className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  const needsCollapse = data.connections.length > MAX_VISIBLE
  const visible = needsCollapse && !expanded ? data.connections.slice(0, MAX_VISIBLE) : data.connections
  const hiddenCount = data.connections.length - MAX_VISIBLE

  return (
    <div className="my-2 space-y-1">
      <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
        <Server className="size-3.5" />
        <span>{data.message}</span>
      </div>
      {visible.map((conn) => (
        <ConnectionRow key={conn.id} connection={conn} />
      ))}
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more connections
        </Button>
      )}
    </div>
  )
}

function ConnectionRow({ connection }: { connection: ConnectionListItemData }) {
  const statusStyle = STATUS_STYLES[connection.status] ?? STATUS_STYLES.INACTIVE
  const dbTypeStyle = DB_TYPE_STYLES[connection.databaseType] ?? 'text-muted-foreground'

  return (
    <div className="rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden border-l-[3px] border-l-amber-500 dark:border-l-amber-400">
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        <div className="flex items-center justify-center size-6 rounded-md shrink-0 bg-amber-100 dark:bg-amber-900/50">
          <Database className="size-3 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground truncate">{connection.name}</span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', statusStyle)}>
              {connection.status}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
            <span className={cn('font-mono font-medium', dbTypeStyle)}>{connection.databaseType}</span>
            <span>{connection.host}</span>
            <span className="font-mono">{connection.databaseName}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
